import {
  BadgeType,
  BasicDetailsFormFields,
  BioFormFields,
  ProfileDetailsFormFields,
} from "../types";
import { FormContext, useForm } from "react-hook-form";
import { InstructorBusinessProfile } from "@hubcapp/types";
import { LANGUAGES } from "@ds-developer/constants";
import {
  checkBioGeneratingLimit,
  instructorBusinessProfileToProfileDetailsFormFields,
} from "../helpers";
import { useAuth } from "context/AuthProvider";
import { useHistory, useLocation } from "react-router-dom";
import {
  useMutationGenerateInstructorBio,
  useMutationSaveInstructorBusinessProfile,
} from "./hooks";
import { useSnackbar } from "notistack";
import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton/IconButton";
import ProfileDetails from "./ProfileDetails";
import React, { useEffect, useState } from "react";
import useQueryInstructorBusinessProfile from "../hooks/useQueryInstructorBusinessProfile";
import validationSchema from "./validationSchema";

interface LocationState {
  data: InstructorBusinessProfile;
}

const StateProvider = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [additionalLanguageOptions, setAdditionalLanguageOptions] = useState<
    string[]
  >([]);
  const { instructor } = useAuth();
  const [initialValues, setInitialValues] = useState<ProfileDetailsFormFields>({
    displayName: null,
    additionalLanguages: null,
    badgeDate: null,
    teaching_enjoyment: null,
    teaching_style: null,
    most_rewarding_aspect: null,
    self_description: null,
    free_time_activities: null,
  });
  const [showBioForm, setShowBioForm] = useState(false);
  const [badgeType, setBadgeType] = useState<BadgeType>(BadgeType.Green);
  const [isBioGenerationDisabled, setIsBioGenerationDisabled] = useState(true);

  const action = () => (
    <IconButton
      aria-label="close"
      color="inherit"
      onClick={() => closeSnackbar()}
    >
      <CloseIcon />
    </IconButton>
  );

  const form = useForm<ProfileDetailsFormFields>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: initialValues,
    validationSchema,
  });
  const {
    watch,
    setValue,
    register,
    reset,
    handleSubmit,
    errors,
    triggerValidation,
  } = form;

  const onQueryCompleted = () => {
    const data = queryData?.getInstructorBusinessProfile;
    if (data) {
      const profileDetailsData = instructorBusinessProfileToProfileDetailsFormFields(
        data,
      );
      setBadgeType(
        data.badgeType === "Pink" ? BadgeType.Pink : BadgeType.Green,
      );
      setInitialValues(profileDetailsData);
      reset(profileDetailsData);
    }
  };

  const { data: queryData, loading } = useQueryInstructorBusinessProfile({
    onCompleted: onQueryCompleted,
    fetchPolicy: "network-only",
    variables: {
      personKey: instructor.id,
    },
    skip: !!location.state,
  });

  useEffect(() => {
    let data: InstructorBusinessProfile | undefined;
    if (location.state?.data) {
      data = location.state.data;
    } else {
      data = queryData?.getInstructorBusinessProfile;
    }
    if (!data) return;
    const profileDetailsData = instructorBusinessProfileToProfileDetailsFormFields(
      data,
    );
    setInitialValues(profileDetailsData);
    reset(profileDetailsData);
    setBadgeType(data.badgeType === "Pink" ? BadgeType.Pink : BadgeType.Green);
  }, [location.state, queryData, reset]);

  const fullAdditionalLanguageOptions = LANGUAGES; // TODO: fetch from API

  useEffect(() => {
    const initialSelectedLanguages = initialValues.additionalLanguages;
    if (initialSelectedLanguages) {
      const newOptions = fullAdditionalLanguageOptions.filter(
        language => !initialSelectedLanguages.includes(language),
      );
      setAdditionalLanguageOptions(newOptions);
    } else {
      setAdditionalLanguageOptions(fullAdditionalLanguageOptions);
    }
    register("displayName");
    register("additionalLanguages");
    register("badgeDate");
    register("teaching_enjoyment");
    register("teaching_style");
    register("most_rewarding_aspect");
    register("self_description");
    register("free_time_activities");
    register("bio");
  }, [register, initialValues]);

  const handleAdditionalLanguagesChange = (value: string[]) => {
    setValue("additionalLanguages", value);
    const newOptions = fullAdditionalLanguageOptions.filter(
      language => !value.includes(language),
    );
    setAdditionalLanguageOptions(newOptions);
  };

  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("displayName", e.target.value);
  };

  const handleBadgeDateChange = (badgeDate: string) => {
    const value = badgeDate ? new Date(badgeDate) : null;
    setValue("badgeDate", value);
    triggerValidation("badgeDate");
  };

  const handleQuestionChange = (key: string, newValues: string[]) => {
    if (key && newValues) {
      setValue(key, newValues);
    }
  };

  const handleEditAnswersClick = () => {
    setShowBioForm(true);
  };

  const handleClearBioClick = () => {
    setValue("badgeDate", null);
    setValue("teaching_enjoyment", null);
    setValue("teaching_style", null);
    setValue("most_rewarding_aspect", null);
    setValue("self_description", null);
    setValue("free_time_activities", null);
    setValue("bio", null);

    setShowBioForm(true);
  };

  const basicDetails: BasicDetailsFormFields = {
    displayName: watch("displayName"),
    additionalLanguages: watch("additionalLanguages"),
  };

  const bioValues: BioFormFields = {
    badgeDate: watch("badgeDate"),
    teaching_enjoyment: watch("teaching_enjoyment"),
    teaching_style: watch("teaching_style"),
    most_rewarding_aspect: watch("most_rewarding_aspect"),
    self_description: watch("self_description"),
    free_time_activities: watch("free_time_activities"),
    bio: watch("bio"),
  };

  const handleBackClick = () => {
    reset();
    history.push("/my-business-profile");
  };

  const [
    saveInstructorBusinessProfile,
  ] = useMutationSaveInstructorBusinessProfile();

  const onSubmit = async (data: ProfileDetailsFormFields) => {
    try {
      const response = await saveInstructorBusinessProfile({
        variables: {
          input: { ...data, personKey: instructor.id },
        },
      });
      if (response.data.saveInstructorBusinessProfile.success === false) {
        enqueueSnackbar(
          "There was an error saving your profile. Please try again.",
          {
            variant: "error",
            role: "alert",
          },
        );
        return;
      }
      enqueueSnackbar(
        "We wanted to let you know that any updates will be published at midnight. Your profile will reflect these changes within the next 24 hours.",
        {
          variant: "success",
          role: "alert",
          persist: true,
          action,
        },
      );
      history.push("/my-business-profile");
    } catch (error) {
      console.error("Error saving profile:", error);
      enqueueSnackbar(
        "There was an error saving your profile. Please try again.",
        {
          variant: "error",
          role: "alert",
        },
      );
    }
  };

  const [
    generateInstructorBio,
    { loading: isGeneratingBio },
  ] = useMutationGenerateInstructorBio({
    variables: {
      input: {
        personKey: instructor.id,
        displayName: basicDetails.displayName ?? "",
        badgeDate: bioValues.badgeDate?.toLocaleDateString("en-GB"),
        teaching_enjoyment: bioValues.teaching_enjoyment,
        teaching_style: bioValues.teaching_style,
        most_rewarding_aspect: bioValues.most_rewarding_aspect,
        self_description: bioValues.self_description,
        free_time_activities: bioValues.free_time_activities,
      },
    },
    onCompleted: data => {
      setValue("bio", data.generateInstructorBio);
      setShowBioForm(false);
      enqueueSnackbar("Bio generated successfully", {
        variant: "success",
        role: "alert",
      });
    },
    onError: () => {
      enqueueSnackbar(
        "There was an error generating your bio. Please try again.",
        {
          variant: "error",
          role: "alert",
        },
      );
    },
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { badgeDate, ...otherBioValues } = bioValues;
    setIsBioGenerationDisabled(
      Object.values(otherBioValues).every(
        value => !value || (Array.isArray(value) && value.length === 0),
      ),
    );
  }, [bioValues]);

  const onGenerateBioClick = async () => {
    const hasBioGeneratingLimit = checkBioGeneratingLimit();
    if (!hasBioGeneratingLimit) {
      enqueueSnackbar(
        "You have reached the limit for generating bio, please try again in a minute.",
        {
          variant: "warning",
          role: "alert",
        },
      );
      return;
    }
    generateInstructorBio();
  };

  return (
    <FormContext {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ProfileDetails
          onAdditionalLanguageChange={handleAdditionalLanguagesChange}
          onDisplayNameChange={handleDisplayNameChange}
          basicDetails={basicDetails}
          additionalLanguageOptions={additionalLanguageOptions}
          bioValues={bioValues}
          errors={errors}
          badgeType={badgeType}
          onBadgeDateChange={handleBadgeDateChange}
          onQuestionChange={handleQuestionChange}
          onBackClicked={handleBackClick}
          onGenerateBioClick={onGenerateBioClick}
          isBioGenerationDisabled={isBioGenerationDisabled}
          loading={loading}
          isGeneratingBio={isGeneratingBio}
          onEditAnswersClick={handleEditAnswersClick}
          onClearBioClick={handleClearBioClick}
          showBioForm={showBioForm}
        />
      </form>
    </FormContext>
  );
};

export default StateProvider;
